import { formateInputNumber } from '@leprecoins/utility';
import { BoxProps, InputProps, NumberInput, Select, TagsInput, TextInput } from '@mantine/core';
import clsx from 'clsx';
import { useUnit, useStoreMap } from 'effector-react';
import { forwardRef, ReactNode, useState } from 'react';
import { DynamicForm } from 'shared/lib';

export type DynamicFormFieldProps = {
	fieldKey: string;
	label?: string;
	placeholder?: string;
	error?: ReactNode;
	className?: string;
} & BoxProps &
	Pick<
		InputProps,
		'rightSection' | 'rightSectionPointerEvents' | 'leftSection' | 'leftSectionPointerEvents'
	>;

export const createDynamicFormField = (model: DynamicForm) => {
	const DynamicFormField = forwardRef<HTMLInputElement, DynamicFormFieldProps>(
		({ fieldKey, className, ...props }, ref) => {
			const change = useUnit(model.change);

			const [search, setSearch] = useState('');

			const field = useStoreMap({
				store: model.$fields,
				keys: [fieldKey],
				fn: (fields) => fields[fieldKey]!,
			});

			if (field.type === 'number')
				return (
					<NumberInput
						ref={ref}
						data-test-id={`form-field-${fieldKey}-number`}
						{...props}
						disabled={field.disabled}
						value={field.value}
						className={clsx('dynamic-form-field', 'dynamic-form-field-number', className)}
						onChange={(value) => change({ key: fieldKey, type: field.type, value: Number(value) })}
					/>
				);

			if (field.type === 'string')
				return (
					<TextInput
						ref={ref}
						data-test-id={`form-field-${fieldKey}-string`}
						{...props}
						disabled={field.disabled}
						value={field.value}
						className={clsx('dynamic-form-field', 'dynamic-form-field-string', className)}
						onChange={(e) => change({ key: fieldKey, type: field.type, value: e.target.value })}
					/>
				);

			if (field.type === 'array')
				return (
					<TagsInput
						ref={ref}
						data-test-id={`form-field-${fieldKey}-array`}
						{...props}
						disabled={field.disabled}
						value={field.value.map((value) => value.toString())}
						className={clsx('dynamic-form-field', 'dynamic-form-field-array', className)}
						searchValue={search}
						onChange={(values) =>
							change({ key: fieldKey, type: field.type, value: values.map(Number) })
						}
						onSearchChange={(v) => setSearch(formateInputNumber(v, null, null, false, 2))}
					/>
				);

			if (field.type === 'select') {
				return (
					<Select
						ref={ref}
						clearable={false}
						data-test-id={`form-field-${fieldKey}-select`}
						{...props}
						disabled={field.disabled}
						value={String(field.value)}
						className={clsx('dynamic-form-field', 'dynamic-form-field-select', className)}
						data={field.options}
						onChange={(value) => {
							if (value) {
								change({ key: fieldKey, type: field.type, value: Number(value) });
							}
						}}
					/>
				);
			}

			return null;
		},
	);

	return DynamicFormField;
};
